import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useContent } from '../hooks/useContent';
import { useSeo } from '../hooks/useSeo';
import { AdminControls } from '../components/AdminControls';
import { GeneratorForm } from '../components/GeneratorForm';
import { NameList } from '../components/NameList';
import { UpgradePro } from '../components/UpgradePro';
import { PdfIcon } from '../components/icons/PdfIcon';
import { generateBrandNames } from '../services/nameGenerationService';
import { exportToPdf } from '../services/pdfService';
import * as authService from '../services/authService';
import type { PageContent, UserInputs, GeneratedName, ProPlan } from '../types';
import { MAX_FREE_GENERATIONS } from '../constants';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { initGA, trackPageview } from '../src/gtag';
import { linkifyHtml } from '../utils/linkify';
import {
  INDUSTRY_LANDING_CONFIGS,
  keyToIndustryConfigMap,
  slugToIndustryConfigMap,
} from '../contexts/industryPagesData';

interface LandingPageTemplateProps {
  contentKey: string;
}

export const LandingPageTemplate: React.FC<LandingPageTemplateProps> = ({ contentKey }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { content: allContent, isAdminMode, updatePageContent } = useContent();
  const { user, setUser, setModalView } = useAuth();
  const { showToast } = useToast();

  const industryConfig =
    keyToIndustryConfigMap[contentKey] ||
    slugToIndustryConfigMap[location.pathname] ||
    slugToIndustryConfigMap[location.pathname.replace(/\/$/, '')];

  // Initialize pageContent safely
  const [pageContent, setPageContent] = useState<PageContent>(
    allContent[contentKey] || {
      h1: industryConfig?.h1 || '',
      body: '',
      seo: {
        title: industryConfig?.seoTitle || '',
        description: industryConfig?.seoDescription || '',
        canonical: industryConfig ? `https://www.brandgenius.app${industryConfig.slug}` : '/',
        slug: industryConfig?.slug || '/',
        customHead: '',
        noindex: false,
      },
    }
  );

  const onSeoApplied = useCallback(() => {
    window.dispatchEvent(new Event('app-rendered'));
  }, []);

  useSeo(pageContent, contentKey, onSeoApplied);

  useEffect(() => {
    if (allContent[contentKey]) {
      setPageContent(allContent[contentKey]);
    }
  }, [allContent, contentKey]);

  // --- Brand Name Generator State ---
  const [userInputs, setUserInputs] = useState<UserInputs | null>(null);
  const [generatedNames, setGeneratedNames] = useState<GeneratedName[]>([]);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [genError, setGenError] = useState<string | null>(null);
  const [generationCount, setGenerationCount] = useState<number>(0);
  const [isPro, setIsPro] = useState<boolean>(false);

  // Reset generated names when navigating between different landing pages
  useEffect(() => {
    setGeneratedNames([]);
    setGenError(null);
  }, [contentKey, location.pathname]);

  useEffect(() => {
    if (user) {
      setIsPro(user.isPro);
      setGenerationCount(user.stats?.generationsUsed || 0);
    } else {
      setIsPro(false);
      const count = sessionStorage.getItem('generationCount') || '0';
      setGenerationCount(parseInt(count, 10));
    }
  }, [user]);

  const isLimitReached = generationCount >= MAX_FREE_GENERATIONS && !isPro;

  const handleGenerate = useCallback(
    async (inputs: UserInputs) => {
      if (isLimitReached) {
        setGenError(`You have reached the free limit of ${MAX_FREE_GENERATIONS} generations.`);
        return;
      }

      setIsGenerating(true);
      setGenError(null);
      setUserInputs(inputs);

      try {
        const names = generateBrandNames(inputs);
        setGeneratedNames((prevNames) => [...names, ...prevNames]);

        const newCount = generationCount + 1;
        setGenerationCount(newCount);

        if (user) {
          await authService.addGeneratedNamesToHistory(user.uid, names, inputs);
          await authService.updateUserStat(user.uid, 'namesGenerated', names.length);
          const updatedUser = await authService.updateUserStat(user.uid, 'generationsUsed');
          if (updatedUser) {
            setUser(updatedUser);
          }
        } else {
          sessionStorage.setItem('generationCount', newCount.toString());
        }
      } catch (err) {
        console.error(err);
        setGenError('An error occurred while generating names. Please try again.');
      } finally {
        setIsGenerating(false);
      }
    },
    [generationCount, isLimitReached, user, setUser]
  );

  const handleUpgradeSuccess = useCallback(
    async (plan: ProPlan) => {
      if (user) {
        const updatedUser = await authService.updateUserProStatus(user.uid, true, plan);
        if (updatedUser) {
          setUser(updatedUser);
        }
      }
    },
    [user, setUser]
  );

  const handleDownloadPdf = useCallback(async () => {
    if (userInputs && generatedNames.length > 0) {
      await exportToPdf(
        generatedNames,
        pageContent?.h1 || 'Brand Name Ideas',
        user?.isPro || false,
        user?.email,
        userInputs
      );
      if (user) {
        const updatedUser = await authService.updateUserStat(user.uid, 'pdfsExported');
        if (updatedUser) {
          setUser(updatedUser);
        }
      }
    }
  }, [generatedNames, userInputs, user, setUser, pageContent?.h1]);

  const handleToggleFavorite = async (name: GeneratedName, isCurrentlyFavorited: boolean) => {
    if (!user) {
      setModalView('signup');
      showToast('Please sign up to save names.');
      return;
    }
    try {
      const updatedUser = await authService.toggleFavoriteName(
        user.uid,
        name,
        isCurrentlyFavorited
      );
      if (updatedUser) {
        setUser(updatedUser);
        showToast(isCurrentlyFavorited ? 'Name removed from favorites.' : 'Name saved to favorites!');
      }
    } catch (error) {
      console.error('Failed to update favorites:', error);
      showToast('Could not update favorites. Please try again.');
    }
  };

  const handleSave = async () => {
    const oldSlug = allContent[contentKey]?.seo?.slug;
    const redirectCreated = await updatePageContent(contentKey, pageContent);
    showToast(`${contentKey} page content saved!`);
    if (redirectCreated) {
      const newSlug = pageContent.seo.slug;
      showToast(`Redirect created: ${oldSlug} -> ${newSlug}`);
    }
  };

  useEffect(() => {
    if (!navigator.userAgent.includes('ReactSnap')) {
      initGA();
    }
  }, []);

  useEffect(() => {
    if (!navigator.userAgent.includes('ReactSnap')) {
      trackPageview(location.pathname + location.search);
    }
  }, [location]);

  const sanitizedBody = pageContent?.body ? linkifyHtml(pageContent.body) : '';

  // Related Industry Pages for Internal SEO Linking
  const relatedIndustries = INDUSTRY_LANDING_CONFIGS.filter(
    (cfg) => cfg.key !== industryConfig?.key
  );

  return (
    <div className="w-full max-w-6xl mx-auto py-12 md:py-20 px-4 sm:px-6 lg:px-8 text-[#1F2937]">
      {/* Breadcrumb Navigation */}
      <nav aria-label="Breadcrumb" className="max-w-4xl mx-auto mb-6 text-sm text-gray-500 flex items-center gap-2">
        <Link to="/" className="hover:text-[#4F46E5] transition-colors">Home</Link>
        <span>/</span>
        <Link to="/industries/" className="hover:text-[#4F46E5] transition-colors">Industries</Link>
        <span>/</span>
        <span className="text-gray-800 font-medium truncate">{pageContent?.h1}</span>
      </nav>

      {/* Hero Header */}
      <div className="w-full max-w-4xl mx-auto text-center mb-10">
        {industryConfig && (
          <div className="text-xs font-bold uppercase tracking-widest text-indigo-600 mb-3">
            {industryConfig.category} · Deterministic Naming Engine
          </div>
        )}
        <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600 mb-5">
          {pageContent?.h1}
        </h1>
        {industryConfig?.subtitle && (
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {industryConfig.subtitle}
          </p>
        )}
      </div>

      {/* Interactive Brand Name Generator Form */}
      <div className="w-full max-w-3xl mx-auto mb-14">
        <div className="bg-white shadow-2xl rounded-2xl p-8 md:p-10 border border-gray-100">
          <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-100">
            <div>
              <h2 className="text-lg font-bold text-gray-900">
                Generate {industryConfig?.navLabel || 'Brand Names'} Instantly
              </h2>
              <p className="text-xs text-gray-500">
                Pre-configured for {industryConfig?.defaultIndustryInput || 'your industry'} — customize keywords or style below.
              </p>
            </div>
            <span className="text-xs font-semibold text-indigo-600">
              Instant .com Check Included
            </span>
          </div>

          <GeneratorForm
            onSubmit={handleGenerate}
            isLoading={isGenerating}
            isLimitReached={isLimitReached}
            initialIndustry={industryConfig?.defaultIndustryInput || ''}
            initialKeywords={industryConfig?.defaultKeywordsInput || ''}
            initialStyle={industryConfig?.defaultStyle || 'Modern'}
          />

          <div className="text-center mt-4 text-sm text-[#6B7280]">
            {isPro ? (
              <p>Unlimited generations (Pro User)</p>
            ) : (
              !isLimitReached && (
                <p>
                  Generations left: {Math.max(0, MAX_FREE_GENERATIONS - generationCount)}/
                  {MAX_FREE_GENERATIONS}
                </p>
              )
            )}
          </div>

          {user && !isPro && MAX_FREE_GENERATIONS - generationCount <= 1 && !isLimitReached && (
            <div className="text-center mt-3 p-3 rounded-md text-sm bg-indigo-50 border border-indigo-200">
              <p className="text-indigo-700">
                You’re almost out of free generations — upgrade to Pro for unlimited access!
              </p>
              <button
                onClick={() => navigate('/dashboard/')}
                className="mt-2 inline-block px-4 py-1.5 bg-[#4F46E5] hover:bg-[#6366F1] text-white font-semibold rounded-lg shadow-sm transition-colors duration-300 text-xs"
              >
                Upgrade to Pro
              </button>
            </div>
          )}

          {isLimitReached && !user && (
            <div className="mt-6 text-center bg-indigo-50 border border-indigo-200 p-6 rounded-xl">
              <h3 className="font-bold text-lg text-indigo-700">Free Limit Reached</h3>
              <p className="my-2 text-indigo-600">
                Sign up to continue generating unlimited names.
              </p>
              <button
                onClick={() => setModalView('signup')}
                className="mt-4 inline-block px-6 py-2.5 bg-[#4F46E5] hover:bg-[#6366F1] text-white font-bold rounded-lg shadow-md transition-all duration-300"
              >
                Sign Up Now
              </button>
            </div>
          )}
          {isLimitReached && user && <UpgradePro onUpgradeSuccess={handleUpgradeSuccess} />}
        </div>

        {genError && !isLimitReached && (
          <div
            className="mt-6 text-center bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl"
            role="alert"
          >
            <p>{genError}</p>
          </div>
        )}

        {generatedNames.length > 0 && (
          <div className="mt-8 bg-white shadow-lg rounded-xl p-6 md:p-8 border border-gray-200 relative">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl md:text-2xl font-bold text-[#1F2937]">
                Generated {industryConfig?.navLabel || 'Brand Names'}
              </h2>
              <button
                onClick={handleDownloadPdf}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-[#1F2937] font-semibold rounded-lg shadow-sm transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!userInputs || (!user?.isPro && generatedNames.length === 0)}
              >
                <PdfIcon />
                Download PDF
              </button>
            </div>
            <NameList names={generatedNames} onToggleFavorite={handleToggleFavorite} />
          </div>
        )}
      </div>

      {/* Main Long-Form SEO Content */}
      <div className="w-full max-w-4xl mx-auto">
        <div
          className="text-lg text-gray-600 leading-relaxed prose max-w-none mx-auto mb-12"
          dangerouslySetInnerHTML={{ __html: sanitizedBody }}
        />

        {isAdminMode && pageContent && (
          <AdminControls
            pageId={contentKey}
            pageContent={pageContent}
            onContentChange={setPageContent}
            onSave={handleSave}
          />
        )}

        {/* Brand Audit Consultant CTA */}
        <div className="my-14 bg-gradient-to-br from-indigo-50 to-white rounded-2xl p-8 md:p-10 border border-indigo-100 text-center shadow-sm">
          <h2 className="text-2xl md:text-3xl font-bold text-indigo-900 mb-3">
            Audit Your Shortlisted {industryConfig?.defaultIndustryInput || 'Brand'} Name
          </h2>
          <p className="text-indigo-700 mb-6 max-w-2xl mx-auto text-base md:text-lg">
            Before registering your domain or filing a trademark, run your favorite name candidate through our deterministic Brand Audit Engine to score its phonetic clarity, linguistic safety, and digital readiness.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button
              onClick={() => navigate('/brand-audit/')}
              className="px-8 py-4 bg-[#4F46E5] hover:bg-[#4338CA] text-white font-bold rounded-xl shadow-md hover:shadow-lg transition-all duration-300"
            >
              Run Free Brand Name Audit
            </button>
            <button
              onClick={() => navigate('/social-media-audit/')}
              className="px-8 py-4 bg-white hover:bg-gray-50 text-indigo-700 border border-indigo-200 font-bold rounded-xl shadow-sm transition-all duration-300"
            >
              Run Instagram Brand Audit
            </button>
          </div>
        </div>

        {/* Internal SEO Linking Architecture: Related Industry Generators */}
        <section className="pt-10 border-t border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-6">
            <div>
              <h2 className="text-2xl font-extrabold text-gray-900">
                Explore Specialized Industry Brand Name Generators
              </h2>
              <p className="text-sm text-gray-500">
                Choose your specific vertical for tailored linguistic patterns, naming frameworks, and curated examples.
              </p>
            </div>
            <Link
              to="/industries/"
              className="text-sm font-bold text-[#4F46E5] hover:underline whitespace-nowrap"
            >
              View All {INDUSTRY_LANDING_CONFIGS.length} Industries →
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {relatedIndustries.map((item) => (
              <Link
                key={item.key}
                to={item.slug}
                className="group p-4 bg-white hover:bg-indigo-50/40 rounded-xl border border-gray-200 hover:border-indigo-300 transition-all shadow-sm"
              >
                <div className="text-xs text-gray-400 mb-1">{item.category}</div>
                <div className="font-bold text-gray-900 group-hover:text-[#4F46E5] transition-colors text-sm">
                  {item.h1}
                </div>
              </Link>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};
